/* ============================================================
   MM-PERSONA-QUIZ-LOADER
   Keeps the full quiz DOM, CSS and module out of the initial
   page work. Existing timer/scroll/exit/manual triggers remain.
   ============================================================ */
(function () {
  'use strict';

  const STORAGE_SEEN = 'mm_persona_quiz_seen_at';
  const STORAGE_COMPLETED = 'mm_persona_quiz_completed';
  const STORAGE_DISMISSED = 'mm_persona_dismissed';
  const WISHLIST_SUBSCRIBED = 'mm_wishlist_subscribed';
  const EMAIL_SESSION = 'mm_pq_email_session';
  const FREQUENCY_DAYS = 30;
  const TRIGGER_DELAY_MS = 25000;
  const PRODUCT_TRIGGER_DELAY_MS = 60000;
  const SCROLL_THRESHOLD_PCT = 60;
  const config = window.MMPersonaQuizConfig || {};

  const SUPPRESSED_PATTERNS = [
    /^\/cart/,
    /^\/checkout/,
    /^\/account/,
    /^\/pages\/lista-de-favoritos/,
    /^\/pages\/mi-lista-de-regalos/,
    /^\/pages\/crear-lista-de-regalos/,
    /^\/pages\/lista-compartida/,
  ];

  let loadingPromise = null;
  let moduleReady = false;
  let autoFired = false;
  let timerId = null;
  let scrollHandler = null;
  let exitHandler = null;
  let scrollFrame = null;

  function readStorage(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function writeStorage(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  function readSession(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function writeSession(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  // Bezoek vanuit een Klaviyo-mail: de rest van deze sessie geen popup (die mensen staan al op de lijst).
  function markEmailVisit() {
    try {
      const params = new URLSearchParams(window.location.search);
      const source = (params.get('utm_source') || '').toLowerCase();
      const medium = (params.get('utm_medium') || '').toLowerCase();
      if (params.has('_kx') || source.indexOf('klaviyo') !== -1 || medium === 'email') {
        writeSession(EMAIL_SESSION, '1');
      }
    } catch (e) { /* ignore */ }
  }

  // Browser die Klaviyo al kent (na een mailklik of een eerder formulier): cookie __kla_id bevat dan $exchange_id.
  function isKnownKlaviyoProfile() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (!match) return false;
      const data = JSON.parse(atob(decodeURIComponent(match[1])));
      return Boolean(data && (data.$exchange_id || data.$email));
    } catch (e) {
      return false;
    }
  }

  // Wie al iets in het mandje heeft, krijgt geen kortingspopup meer (die koopt al; de code kost dan alleen marge).
  function cartHasItems() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) { return Boolean(cart && cart.item_count > 0); })
      .catch(function () { return false; });
  }

  function clearFrequencyState() {
    writeStorage(STORAGE_SEEN, '');
    writeStorage(STORAGE_COMPLETED, '');
    writeStorage(STORAGE_DISMISSED, '');
  }

  function isProductPage() {
    return /^\/products\//.test(window.location.pathname);
  }

  function isMobile() {
    return window.matchMedia
      ? !window.matchMedia('(min-width: 750px)').matches
      : window.innerWidth < 750;
  }

  function shouldSuppressAuto() {
    if (readStorage(STORAGE_COMPLETED) === '1') return true;

    const seenAt = parseInt(readStorage(STORAGE_SEEN) || '0', 10);
    if (seenAt && Date.now() - seenAt < FREQUENCY_DAYS * 86400000) return true;

    const dismissedAt = parseInt(readStorage(STORAGE_DISMISSED) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < FREQUENCY_DAYS * 86400000) return true;

    const path = window.location.pathname;
    for (let i = 0; i < SUPPRESSED_PATTERNS.length; i++) {
      if (SUPPRESSED_PATTERNS[i].test(path)) return true;
    }

    if (readSession(EMAIL_SESSION) === '1') return true;
    if (isKnownKlaviyoProfile()) return true;

    if (readStorage(WISHLIST_SUBSCRIBED) === '1') return true;
    try {
      if (window.MMWishlist && typeof window.MMWishlist.isSubscribed === 'function') {
        if (window.MMWishlist.isSubscribed()) return true;
      }
    } catch (e) { /* ignore */ }

    return false;
  }

  function whenDomReady() {
    if (document.readyState !== 'loading') return Promise.resolve();
    return new Promise(function (resolve) {
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
    });
  }

  function mountModal() {
    if (document.querySelector('[data-mm-pq-modal]')) return true;
    const template = document.querySelector('template[data-mm-pq-template]');
    if (!template || !template.content) return false;
    document.body.appendChild(template.content.cloneNode(true));
    template.remove();
    return true;
  }

  function loadStylesheet() {
    if (document.querySelector('link[data-mm-pq-styles]')) return Promise.resolve();
    if (!config.styleUrl) return Promise.resolve();

    return new Promise(function (resolve) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.styleUrl;
      link.setAttribute('data-mm-pq-styles', '');
      link.onload = resolve;
      link.onerror = resolve;
      document.head.appendChild(link);
    });
  }

  function loadModule(source, force) {
    if (moduleReady && window.MMPersonaQuiz) {
      window.MMPersonaQuiz.open(source || 'manual');
      return Promise.resolve(window.MMPersonaQuiz);
    }
    if (loadingPromise) return loadingPromise;

    config.triggerSource = source || 'manual';
    config.force = force === true;

    loadingPromise = whenDomReady()
      .then(function () {
        if (!mountModal()) throw new Error('quiz_template_missing');
        return loadStylesheet();
      })
      .then(function () {
        return new Promise(function (resolve, reject) {
          const script = document.createElement('script');
          script.src = config.scriptUrl;
          script.async = true;
          script.setAttribute('data-mm-pq-module', '');
          script.onload = function () {
            moduleReady = true;
            resolve(window.MMPersonaQuiz || null);
          };
          script.onerror = function () {
            script.remove();
            loadingPromise = null;
            reject(new Error('quiz_module_failed'));
          };
          document.head.appendChild(script);
        });
      })
      .catch(function (error) {
        console.warn('[mm-pq-loader] load failed', error);
        return null;
      });

    return loadingPromise;
  }

  function clearAutoTriggers() {
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    if (scrollFrame !== null) {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
    }
    if (exitHandler) {
      document.removeEventListener('mouseleave', exitHandler);
      exitHandler = null;
    }
  }

  function fireAuto(source) {
    if (autoFired) return;
    autoFired = true;
    clearAutoTriggers();
    cartHasItems().then(function (hasItems) {
      if (hasItems) {
        try {
          if (typeof window.gtag === 'function') window.gtag('event', 'mm_persona_quiz_skipped', { reason: 'cart_has_items', trigger: source });
        } catch (e) { /* ignore */ }
        return;
      }
      loadModule(source, false);
    });
  }

  function attachAutoTriggers() {
    if (shouldSuppressAuto()) return;

    timerId = window.setTimeout(function () {
      fireAuto('timer');
    }, isProductPage() ? PRODUCT_TRIGGER_DELAY_MS : TRIGGER_DELAY_MS);

    if (!isProductPage()) {
      scrollHandler = function () {
        if (scrollFrame !== null) return;
        scrollFrame = window.requestAnimationFrame(function () {
          scrollFrame = null;
          const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
          const scrollPct = (window.scrollY / scrollable) * 100;
          if (scrollPct >= SCROLL_THRESHOLD_PCT) fireAuto('scroll');
        });
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
    }

    if (!isMobile()) {
      exitHandler = function (event) {
        if (event.clientY <= 0) fireAuto('exit_intent');
      };
      document.addEventListener('mouseleave', exitHandler);
    }
  }

  function handleManualOpen(event) {
    const trigger = event.target && event.target.closest
      ? event.target.closest('[data-mm-pq-open]')
      : null;
    if (!trigger) return;
    event.preventDefault();
    clearAutoTriggers();
    const source = trigger.getAttribute('data-mm-pq-source') || 'manual';
    loadModule(source, false);
  }

  function boot() {
    document.addEventListener('click', handleManualOpen);
    markEmailVisit();

    let qaValue = null;
    try { qaValue = new URLSearchParams(window.location.search).get('mm_pq'); } catch (e) { /* ignore */ }

    if (qaValue === 'reset') {
      clearFrequencyState();
      attachAutoTriggers();
      return;
    }
    if (qaValue !== null) {
      clearFrequencyState();
      autoFired = true;
      loadModule('qa', true);
      return;
    }

    attachAutoTriggers();
  }

  window.MMPersonaQuizLoader = {
    open: function (source) {
      clearAutoTriggers();
      return loadModule(source || 'manual', false);
    },
    reset: clearFrequencyState,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
